import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ImageUploader from './ImageUploader';

const { FiArrowLeft, FiSave, FiX, FiAlertCircle, FiChevronUp, FiChevronDown } = FiIcons;

const BrandSettings = ({ brandSettings, onSaveBrandSettings, onBack }) => {
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    tagline: '',
    primaryColor: '#6366F1', // Default indigo color
    secondaryColor: '#8B5CF6' // Default purple color
  });
  const [errors, setErrors] = useState({});
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(true);
  const formRef = useRef(null);

  // Initialize form data from props
  useEffect(() => {
    if (brandSettings) {
      setFormData({
        name: brandSettings.name || 'CouponHub',
        logo: brandSettings.logo || '',
        tagline: brandSettings.tagline || 'Best deals in your region',
        primaryColor: brandSettings.primaryColor || '#6366F1',
        secondaryColor: brandSettings.secondaryColor || '#8B5CF6'
      });
    }
  }, [brandSettings]);

  // Scroll position detection
  useEffect(() => {
    const handleScroll = () => {
      if (!formRef.current) return;
      
      const { scrollTop, scrollHeight, clientHeight } = formRef.current;
      setShowScrollUp(scrollTop > 20);
      setShowScrollDown(scrollTop + clientHeight < scrollHeight - 20);
    };

    const formElement = formRef.current;
    if (formElement) {
      formElement.addEventListener('scroll', handleScroll);
      // Initial check
      handleScroll();
    }

    return () => {
      if (formElement) {
        formElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleLogoChange = (logoData) => {
    setFormData(prev => ({
      ...prev,
      logo: logoData
    }));
    
    // Clear logo error if it exists
    if (errors.logo) {
      setErrors(prev => ({
        ...prev,
        logo: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Brand name is required';
    if (formData.name.trim().length > 30) newErrors.name = 'Brand name must be 30 characters or less';
    
    if (formData.tagline && formData.tagline.length > 50) {
      newErrors.tagline = 'Tagline must be 50 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSaveBrandSettings(formData);
      // Show success feedback
      const successEl = document.getElementById('brand-settings-success');
      if (successEl) {
        successEl.classList.remove('hidden');
        setTimeout(() => {
          successEl.classList.add('hidden');
        }, 3000);
      }
    }
  };

  const scrollToTop = () => {
    if (formRef.current) {
      formRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const scrollToBottom = () => {
    if (formRef.current) {
      formRef.current.scrollTo({
        top: formRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col h-full"
    >
      <div className="flex items-center mb-5">
        <button 
          onClick={onBack} 
          className="mr-3 text-gray-500 hover:text-gray-700 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="text-lg" />
        </button>
        <h2 className="text-xl font-bold text-gray-800">Brand Settings</h2>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {showScrollUp && (
          <button 
            onClick={scrollToTop}
            className="absolute top-0 left-1/2 transform -translate-x-1/2 z-10 bg-white bg-opacity-80 rounded-full p-1 shadow-md hover:bg-opacity-100 transition-all"
          >
            <SafeIcon icon={FiChevronUp} className="text-gray-600" />
          </button>
        )}
        
        {showScrollDown && (
          <button 
            onClick={scrollToBottom}
            className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-10 bg-white bg-opacity-80 rounded-full p-1 shadow-md hover:bg-opacity-100 transition-all"
          >
            <SafeIcon icon={FiChevronDown} className="text-gray-600" />
          </button>
        )}

        <motion.form
          ref={formRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onSubmit={handleSubmit}
          className="bg-white rounded-lg p-5 overflow-auto max-h-[calc(90vh-120px)] relative"
        >
          <div className="mb-6">
            <p className="text-gray-600 text-sm">
              Customize your application's branding to match your company's identity.
            </p>
            <div id="brand-settings-success" className="mt-3 p-2 bg-green-50 border border-green-200 rounded-md text-green-600 text-sm hidden">
              Brand settings saved successfully!
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., CouponHub"
                className={`w-full p-2 border rounded-lg ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <SafeIcon icon={FiAlertCircle} className="mr-1" />
                  {errors.name}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                This will appear in the header of your application.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tagline
              </label>
              <input
                type="text"
                name="tagline"
                value={formData.tagline}
                onChange={handleChange}
                placeholder="e.g., Best deals in your region"
                className={`w-full p-2 border rounded-lg ${errors.tagline ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.tagline && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <SafeIcon icon={FiAlertCircle} className="mr-1" />
                  {errors.tagline}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                A short phrase that describes your brand.
              </p>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brand Logo
            </label>
            <div className="max-w-md">
              <ImageUploader
                currentImageUrl={formData.logo}
                onImageChange={handleLogoChange}
              />
            </div>
            {errors.logo && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <SafeIcon icon={FiAlertCircle} className="mr-1" />
                {errors.logo}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              For best results, use a square image with transparent background (PNG format).
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  name="primaryColor"
                  value={formData.primaryColor}
                  onChange={handleChange}
                  className="w-10 h-10 rounded border p-0 cursor-pointer"
                />
                <input
                  type="text"
                  name="primaryColor"
                  value={formData.primaryColor}
                  onChange={handleChange}
                  className="flex-1 p-2 border rounded-lg border-gray-300 font-mono"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Main color used throughout the application.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secondary Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  name="secondaryColor"
                  value={formData.secondaryColor}
                  onChange={handleChange}
                  className="w-10 h-10 rounded border p-0 cursor-pointer"
                />
                <input
                  type="text"
                  name="secondaryColor"
                  value={formData.secondaryColor}
                  onChange={handleChange}
                  className="flex-1 p-2 border rounded-lg border-gray-300 font-mono"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Used for accents and gradients.
              </p>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preview
            </label>
            <div className="border rounded-lg p-4">
              <div 
                className="rounded-xl p-4 flex items-center space-x-3 mb-3"
                style={{ 
                  background: `linear-gradient(to right, ${formData.primaryColor}, ${formData.secondaryColor})` 
                }}
              >
                {formData.logo ? (
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                    <img 
                      src={formData.logo} 
                      alt="Brand logo" 
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiIcons.FiGift} className="text-xl" style={{ color: formData.primaryColor }} />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-white">{formData.name}</h3>
                  <p className="text-xs text-white opacity-90">{formData.tagline}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg text-white text-sm"
                  style={{ backgroundColor: formData.primaryColor }}
                >
                  Primary Button
                </button>
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg text-sm"
                  style={{ 
                    color: formData.secondaryColor, 
                    backgroundColor: `${formData.secondaryColor}20` 
                  }}
                >
                  Secondary Button
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-1"
            >
              <SafeIcon icon={FiX} />
              <span>Cancel</span>
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center space-x-1"
            >
              <SafeIcon icon={FiSave} />
              <span>Save Changes</span>
            </button>
          </div>
        </motion.form>
      </div>
    </motion.div>
  );
};

export default BrandSettings;